import React, { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../api';
import { useAuth } from '../AuthContext';
import { RefreshCw, AlertTriangle, ChevronDown, ChevronRight, Plus, Pencil, Trash2, Info, Download } from 'lucide-react';
import {
  msgErrore, fmtEur, fmtN, fmtPerc, fmtData, Rapporto, Sezione, card, th, thSx, td, tdSx, pulsante, errore, testoBlob,
  vociCanale, voceCanale,
} from './campagneComuni';
import CampagneAdmin from './CampagneAdmin';
import EsportaLead from './EsportaLead';

// Pagina Campagne (brief §6, Fase 5): report per canale e per tipo di ricorso, segnalazioni,
// costi manuali e, per chi ha campagne.gestisci, le sezioni admin. Il periodo e i filtri stanno
// nell'URL (?pagina=campagne&dal=…&al=…&tipo=…&canale=…), così il report si riapre uguale.

const COLORI = { filtri: '#4A5568', segnalazioni: '#B7791F', sintesi: '#2B6CB0', canali: '#16A085',
  tipi: '#6B46C1', costi: '#C05621' };

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function scorciatoie() {
  const oggi = new Date();
  const primo = new Date(oggi.getFullYear(), oggi.getMonth(), 1);
  const primoScorso = new Date(oggi.getFullYear(), oggi.getMonth() - 1, 1);
  const ultimoScorso = new Date(oggi.getFullYear(), oggi.getMonth(), 0);
  const trenta = new Date(oggi); trenta.setDate(oggi.getDate() - 29);
  return [
    { etichetta: 'Mese corrente', dal: iso(primo), al: iso(oggi) },
    { etichetta: 'Mese scorso', dal: iso(primoScorso), al: iso(ultimoScorso) },
    { etichetta: 'Ultimi 30 giorni', dal: iso(trenta), al: iso(oggi) },
    { etichetta: 'Anno', dal: `${oggi.getFullYear()}-01-01`, al: iso(oggi) },
  ];
}

const DATA_OK = /^\d{4}-\d{2}-\d{2}$/;

function filtriDaUrl() {
  const q = new URLSearchParams(window.location.search);
  const [mese] = scorciatoie();
  return {
    dal: DATA_OK.test(q.get('dal') || '') ? q.get('dal') : mese.dal,
    al: DATA_OK.test(q.get('al') || '') ? q.get('al') : mese.al,
    tipo: q.get('tipo') || '',
    canale: q.get('canale') || '',
  };
}

function scriviUrl(f) {
  const q = new URLSearchParams({ pagina: 'campagne', dal: f.dal, al: f.al });
  if (f.tipo) q.set('tipo', f.tipo);
  if (f.canale) q.set('canale', f.canale);
  window.history.replaceState(null, '', `${window.location.pathname}?${q.toString()}`);
}

// --- Celle ---------------------------------------------------------------------------------

function Scarto({ r }) {
  if (r.scarto === null || r.scarto === undefined) return fmtN(null);
  if (r.scarto < 0) {
    return <span title="Conversioni dichiarate inferiori ai lead: la piattaforma le attribuisce con ritardo">—</span>;
  }
  const segno = r.scarto > 0 ? '+' : '';
  const testo = `${segno}${fmtN(r.scarto).toString()}` + (r.scarto_perc !== null && r.scarto_perc !== undefined
    ? ` (${segno}${(r.scarto_perc * 100).toLocaleString('it-IT', { maximumFractionDigits: 0 })}%)` : '');
  return (
    <span style={{ color: r.evidenza ? '#C53030' : undefined, fontWeight: r.evidenza ? 600 : undefined }}
      title={r.evidenza ? 'Scarto conversioni/lead oltre la soglia' : undefined}>
      {r.evidenza && <AlertTriangle size={12} style={{ marginRight: 3, verticalAlign: '-1px' }} />}{testo}
    </span>
  );
}

function Cpl({ r, avviso }) {
  return (
    <span title={avviso || undefined}>
      <Rapporto r={r} campo="cpl" fmt={fmtEur} />
      {r.cpl_dal && <div style={{ fontSize: 10, color: '#a0aec0' }}>dal {fmtData(r.cpl_dal)}</div>}
    </span>
  );
}

function RigaCanale({ r, sotto }) {
  const stile = sotto ? { ...td, color: '#4A5568', fontSize: 12 } : td;
  const stileSx = sotto ? { ...tdSx, paddingLeft: 26, color: '#4A5568', fontSize: 12 } : { ...tdSx, fontWeight: 600 };
  const avviso = r.prefisso_non_attivo ? 'Prefisso non ancora attivo per una parte del periodo'
    : r.piattaforma_non_attiva ? 'Piattaforma dei lead non ancora disponibile per una parte del periodo' : null;
  if (r.chiave === 'Adesioni dirette') {
    return (
      <tr>
        <td style={stileSx}>{r.etichetta}</td>
        <td style={td} colSpan={5} /><td style={td}>{fmtN(r.adesioni)}</td><td style={td} colSpan={4} />
      </tr>
    );
  }
  return (
    <tr style={{ background: sotto ? '#FAFBFC' : undefined }}>
      <td style={stileSx}>
        {sotto ? '└ ' : ''}{r.etichetta}
        {avviso && <span title={avviso}><AlertTriangle size={12} color="#B7791F"
          style={{ marginLeft: 5, verticalAlign: '-1px' }} /></span>}
      </td>
      <td style={stile}>{fmtEur(r.spesa)}</td>
      <td style={stile}>{fmtN(r.click)}</td>
      <td style={stile}>{fmtEur(r.cpc)}</td>
      <td style={stile}>
        {fmtN(r.lead)}
        {r.lead !== null && r.lead_invii !== null && r.lead_invii !== r.lead &&
          <span style={{ color: '#a0aec0', fontSize: 11 }}> ({fmtN(r.lead_invii)} invii)</span>}
      </td>
      <td style={stile}><Cpl r={r} avviso={avviso} /></td>
      <td style={stile}>{sotto ? '' : fmtN(r.adesioni ?? null)}</td>
      <td style={stile}>{sotto ? '' : <Rapporto r={r} campo="costo_per_adesione" fmt={fmtEur} />}</td>
      <td style={stile}>{sotto ? '' : <Rapporto r={r} campo="lead_adesioni" fmt={fmtPerc} />}</td>
      <td style={stile}>{fmtN(r.conversioni, 0)}</td>
      <td style={stile}>{sotto ? '' : <Scarto r={r} />}</td>
    </tr>
  );
}

function TabellaCanali({ dati }) {
  const t = dati.totale;
  return (
    <div style={{ ...card, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thSx}>Canale</th><th style={th}>Spesa</th><th style={th}>Click</th><th style={th}>CPC</th>
            <th style={th}>Lead</th><th style={th}>CPL</th><th style={th}>Adesioni</th><th style={th}>€ / adesione</th>
            <th style={th} title="Adesioni del canale / lead del canale, dal giorno da cui si contano le adesioni">Lead → adesioni</th>
            <th style={th}>Conv. dichiarate</th><th style={th}>Scarto</th>
          </tr>
        </thead>
        <tbody>
          {dati.righe.map(r => (
            <React.Fragment key={r.chiave}>
              <RigaCanale r={r} />
              {(r.sottorighe || []).map(s => <RigaCanale key={`${r.chiave}-${s.chiave}`} r={s} sotto />)}
              {r.sottorighe && (
                <tr style={{ background: '#FAFBFC' }}>
                  <td style={{ ...tdSx, paddingLeft: 26, fontSize: 12, color: '#4A5568' }}>└ Conversazioni WhatsApp/Messenger</td>
                  <td style={{ ...td, fontSize: 12 }} colSpan={10}>
                    {fmtN(r.conversazioni ?? null)} conversazioni · costo per conversazione {fmtEur(r.costo_per_conversazione ?? null)}
                    <span style={{ color: '#a0aec0' }}> · non si sommano ai lead</span>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          <tr style={{ background: '#F0FFF4' }}>
            <td style={{ ...tdSx, fontWeight: 700 }}>{t.etichetta}</td>
            <td style={{ ...td, fontWeight: 700 }}>{fmtEur(t.spesa)}</td>
            <td style={{ ...td, fontWeight: 700 }}>{fmtN(t.click)}</td>
            <td style={{ ...td, fontWeight: 700 }}>{fmtEur(t.cpc)}</td>
            <td style={{ ...td, fontWeight: 700 }}>{fmtN(t.lead)}</td>
            <td style={{ ...td, fontWeight: 700 }}><Rapporto r={t} campo="cpl" fmt={fmtEur} /></td>
            <td style={{ ...td, fontWeight: 700 }}>{fmtN(t.adesioni ?? null)}</td>
            <td style={{ ...td, fontWeight: 700 }}><Rapporto r={t} campo="costo_per_adesione" fmt={fmtEur} /></td>
            <td style={{ ...td, fontWeight: 700 }} title="Adesioni uniche / persone uniche">
              <Rapporto r={t} campo="lead_adesioni" fmt={fmtPerc} /></td>
            <td style={{ ...td, fontWeight: 700 }}>{fmtN(t.conversioni, 0)}</td>
            <td style={td} />
          </tr>
        </tbody>
      </table>
      <div style={{ fontSize: 12, color: '#4A5568', marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
        <Info size={13} /> {dati.nota} I lead si contano senza doppioni dentro il canale (email e tipo di ricorso);
        tra parentesi gli invii.
      </div>
    </div>
  );
}

function TabellaTipi({ dati }) {
  const [aperta, setAperta] = useState(false);
  // canali con spesa e conversioni (Google, Meta) in gruppi; gli altri solo con i lead
  const ads = dati.righe.filter(r => r.conversioni !== null && r.conversioni !== undefined);
  const altri = dati.righe.filter(r => !ads.includes(r) && r.chiave !== 'Adesioni dirette');
  return (
    <Sezione titolo="Per tipo di ricorso" colore={COLORI.tipi}
      destra={<button style={pulsante(false)} onClick={() => setAperta(a => !a)}>
        {aperta ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {aperta ? 'Chiudi' : 'Apri'}</button>}>
      {aperta && (
        <div style={{ ...card, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
            <thead>
              <tr>
                <th style={thSx} rowSpan={2}>Tipo</th><th style={th} rowSpan={2}>Contatti</th>
                <th style={th} rowSpan={2}>Adesioni</th><th style={th} rowSpan={2}>Lead → adesioni</th>
                {ads.map(c => <th key={c.chiave} style={{ ...th, textAlign: 'center', borderLeft: '1px solid #e8eaed' }}
                  colSpan={6}>{c.etichetta}</th>)}
                {altri.length > 0 && <th style={{ ...th, textAlign: 'center', borderLeft: '1px solid #e8eaed' }}
                  colSpan={altri.length}>Lead per canale</th>}
              </tr>
              <tr>
                {ads.map(c => <React.Fragment key={c.chiave}>{['Click', 'Spesa', 'Lead', 'CPL', 'Conv.', 'Scarto'].map((x, i) => (
                  <th key={x} style={{ ...th, borderLeft: i === 0 ? '1px solid #e8eaed' : undefined }}>{x}</th>))}</React.Fragment>)}
                {altri.map((c, i) => <th key={c.chiave} style={{ ...th, borderLeft: i === 0 ? '1px solid #e8eaed' : undefined }}>
                  {c.etichetta}</th>)}
              </tr>
            </thead>
            <tbody>
              {dati.per_tipo.map(t => (
                <tr key={t.tipo_id ?? 'senza'}>
                  <td style={tdSx}>{t.tipo}</td>
                  <td style={td}>{fmtN(t.contatti)}</td>
                  <td style={td}>{fmtN(t.adesioni)}</td>
                  <td style={td}><Rapporto r={t} campo="lead_adesioni" fmt={fmtPerc} /></td>
                  {ads.map(c => {
                    const x = t.canali[c.chiave];
                    return x ? (
                      <React.Fragment key={c.chiave}>
                        <td style={{ ...td, borderLeft: '1px solid #f1f3f5' }}>{fmtN(x.click)}</td>
                        <td style={td}>{fmtEur(x.spesa)}</td><td style={td}>{fmtN(x.lead)}</td>
                        <td style={td}><Cpl r={x} /></td><td style={td}>{fmtN(x.conversioni, 0)}</td>
                        <td style={td}><Scarto r={x} /></td>
                      </React.Fragment>
                    ) : <td key={c.chiave} style={{ ...td, borderLeft: '1px solid #f1f3f5' }} colSpan={6} />;
                  })}
                  {altri.map(c => <td key={c.chiave} style={td}>{t.canali[c.chiave] ? fmtN(t.canali[c.chiave].lead) : ''}</td>)}
                </tr>
              ))}
              <tr style={{ background: '#FAF5FF' }}>
                <td style={{ ...tdSx, fontWeight: 700 }}>Totale (persone uniche)</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmtN(dati.totale.lead)}</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmtN(dati.totale.adesioni ?? null)}</td>
                <td style={{ ...td, fontWeight: 700 }}><Rapporto r={dati.totale} campo="lead_adesioni" fmt={fmtPerc} /></td>
                {ads.map(c => (
                  <React.Fragment key={c.chiave}>
                    <td style={{ ...td, fontWeight: 700, borderLeft: '1px solid #f1f3f5' }}>{fmtN(c.click)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmtEur(c.spesa)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmtN(c.lead)}</td>
                    <td style={{ ...td, fontWeight: 700 }}><Cpl r={c} /></td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmtN(c.conversioni, 0)}</td>
                    <td style={{ ...td, fontWeight: 700 }}><Scarto r={c} /></td>
                  </React.Fragment>
                ))}
                {altri.map(c => <td key={c.chiave} style={{ ...td, fontWeight: 700 }}>{fmtN(c.lead)}</td>)}
              </tr>
            </tbody>
          </table>
          <div style={{ fontSize: 12, color: '#4A5568', marginTop: 8 }}>
            La spesa delle campagne senza tipo di ricorso (per esempio le Post Interaction) e i costi senza tipo
            sono solo nel totale del canale.
          </div>
        </div>
      )}
    </Sezione>
  );
}

// --- Costi manuali -------------------------------------------------------------------------

const COSTO_VUOTO = { prefisso: '', canale: '', tipo_ricorso_id: '', dal: '', al: '', importo: '', descrizione: '' };

function CostiManuali({ filtri, periodo, puoGestire, onCambio }) {
  const [costi, setCosti] = useState([]);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState(null);           // null = chiuso; { id?, ...campi }
  const [salvo, setSalvo] = useState(false);
  const referrer = vociCanale(filtri?.prefissi, p => p.fonte === 'referral');      // una voce per canale

  const carica = useCallback(async () => {
    try {
      const r = await API.get('/campagne/costi', { params: { dal: periodo.dal, al: periodo.al } });
      setCosti(r.data); setErr(null);
    } catch (e) { setErr(msgErrore(e, 'Impossibile leggere i costi manuali')); }
  }, [periodo.dal, periodo.al]);
  useEffect(() => { carica(); }, [carica]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const salva = async () => {
    setSalvo(true); setErr(null);
    const corpo = {
      prefisso: form.prefisso || null, canale: form.prefisso ? null : (form.canale || null),
      tipo_ricorso_id: form.tipo_ricorso_id ? Number(form.tipo_ricorso_id) : null,
      dal: form.dal, al: form.al, importo: form.importo, descrizione: form.descrizione || null,
    };
    try {
      if (form.id) await API.patch(`/campagne/costi/${form.id}`, corpo);
      else await API.post('/campagne/costi', corpo);
      setForm(null); await carica(); onCambio();
    } catch (e) { setErr(msgErrore(e, 'Salvataggio non riuscito')); }
    finally { setSalvo(false); }
  };
  const elimina = async c => {
    if (!window.confirm(`Eliminare il costo di ${c.canale} dal ${fmtData(c.dal)} al ${fmtData(c.al)}?`)) return;
    try { await API.delete(`/campagne/costi/${c.id}`); await carica(); onCambio(); }
    catch (e) { setErr(msgErrore(e, 'Eliminazione non riuscita')); }
  };

  return (
    <Sezione titolo="Costi manuali (referrer e offline)" colore={COLORI.costi}
      destra={puoGestire && !form && <button style={pulsante(true)} onClick={() => setForm({ ...COSTO_VUOTO })}>
        <Plus size={14} /> Nuovo costo</button>}>
      {err && <div style={errore}>{err}</div>}
      {form && (
        <div style={{ ...card, marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <label style={{ fontSize: 11, color: '#718096' }}>REFERRER<br />
            <select className="input-filtro" value={form.prefisso} onChange={e => set('prefisso', e.target.value)}>
              <option value="">— nessuno (costo offline) —</option>
              {referrer.map(p => <option key={p.prefisso} value={p.prefisso}>{p.canale}</option>)}
            </select></label>
          {!form.prefisso && <label style={{ fontSize: 11, color: '#718096' }}>CANALE<br />
            <input className="input-filtro" value={form.canale} onChange={e => set('canale', e.target.value)}
              placeholder="es. Volantini" maxLength={100} /></label>}
          <label style={{ fontSize: 11, color: '#718096' }}>TIPO DI RICORSO<br />
            <select className="input-filtro" value={form.tipo_ricorso_id} onChange={e => set('tipo_ricorso_id', e.target.value)}>
              <option value="">— tutti (costo generico) —</option>
              {(filtri?.tipi || []).map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select></label>
          <label style={{ fontSize: 11, color: '#718096' }}>DAL<br />
            <input type="date" className="input-filtro" value={form.dal} onChange={e => set('dal', e.target.value)} /></label>
          <label style={{ fontSize: 11, color: '#718096' }}>AL<br />
            <input type="date" className="input-filtro" value={form.al} onChange={e => set('al', e.target.value)} /></label>
          <label style={{ fontSize: 11, color: '#718096' }}>IMPORTO (€)<br />
            <input type="number" min="0" step="0.01" className="input-filtro" value={form.importo}
              onChange={e => set('importo', e.target.value)} style={{ width: 110 }} /></label>
          <label style={{ fontSize: 11, color: '#718096', flex: 1, minWidth: 200 }}>DESCRIZIONE<br />
            <input className="input-filtro" value={form.descrizione} onChange={e => set('descrizione', e.target.value)}
              maxLength={500} style={{ width: '100%', background: '#FFFBF0' }} /></label>
          <button style={pulsante(true, salvo)} disabled={salvo} onClick={salva}>Salva</button>
          <button style={pulsante(false)} onClick={() => setForm(null)}>Annulla</button>
        </div>
      )}
      <div style={{ ...card, overflowX: 'auto' }}>
        {costi.length === 0 ? <div style={{ fontSize: 13, color: '#718096' }}>Nessun costo manuale inserito.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thSx}>Canale</th><th style={thSx}>Tipo</th><th style={thSx}>Periodo</th>
              <th style={th}>Importo</th><th style={th}>Quota nel periodo</th><th style={thSx}>Descrizione</th>
              {puoGestire && <th style={th} />}
            </tr></thead>
            <tbody>
              {costi.map(c => (
                <tr key={c.id}>
                  <td style={tdSx}>{c.canale}{c.prefisso ? ` [${c.prefisso}]` : ''}</td>
                  <td style={tdSx}>{c.tipo || 'tutti'}</td>
                  <td style={tdSx}>{fmtData(c.dal)} – {fmtData(c.al)}</td>
                  <td style={td}>{fmtEur(c.importo)}</td>
                  <td style={td}>{fmtEur(c.quota_periodo ?? null)}</td>
                  <td style={tdSx}>{c.descrizione}</td>
                  {puoGestire && <td style={td}>
                    <button style={pulsante(false)} title="Modifica" onClick={() => setForm({
                      id: c.id, prefisso: voceCanale(filtri?.prefissi, c.prefisso), canale: c.prefisso ? '' : c.canale,
                      tipo_ricorso_id: c.tipo_ricorso_id ?? '', dal: c.dal, al: c.al, importo: String(c.importo),
                      descrizione: c.descrizione || '' })}><Pencil size={13} /></button>{' '}
                    <button style={pulsante(false)} title="Elimina" onClick={() => elimina(c)}><Trash2 size={13} /></button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Sezione>
  );
}

// --- Pagina --------------------------------------------------------------------------------

export default function Campagne() {
  const { haPermesso } = useAuth();
  const puoGestire = haPermesso('campagne.gestisci');
  const [f, setF] = useState(filtriDaUrl);
  const [filtri, setFiltri] = useState(null);
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(false);
  const [err, setErr] = useState(null);
  const [versione, setVersione] = useState(0);       // sale dopo una modifica: ricarica il report

  useEffect(() => {
    API.get('/campagne/filtri').then(r => setFiltri(r.data))
      .catch(e => setErr(msgErrore(e, 'Impossibile leggere i filtri del report')));
  }, []);

  // periodo e filtri nell'URL; uscendo dalla pagina l'URL torna pulito
  useEffect(() => { scriviUrl(f); }, [f]);
  useEffect(() => () => window.history.replaceState(null, '', window.location.pathname), []);

  const valido = DATA_OK.test(f.dal) && DATA_OK.test(f.al) && f.dal <= f.al;
  const params = useMemo(() => {
    const x = { dal: f.dal, al: f.al };
    if (f.tipo) x.tipo_ricorso_id = f.tipo;
    if (f.canale) x.canale = f.canale;
    return x;
  }, [f.dal, f.al, f.tipo, f.canale]);
  useEffect(() => {
    if (!valido) return undefined;
    let attivo = true;
    setCarico(true); setErr(null);
    API.get('/campagne/report', { params })
      .then(r => { if (attivo) setDati(r.data); })
      .catch(e => { if (attivo) setErr(msgErrore(e, 'Impossibile calcolare il report')); })
      .finally(() => { if (attivo) setCarico(false); });
    return () => { attivo = false; };
  }, [params, valido, versione]);

  // stesso periodo e filtri del report a video; l'errore arriva come Blob e va letto
  const [scarico, setScarico] = useState(false);
  const scaricaExcel = async () => {
    setScarico(true); setErr(null);
    try {
      const r = await API.get('/campagne/report/excel', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_campagne_${f.dal}_${f.al}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let detail = null;
      try { detail = JSON.parse(await testoBlob(e.response.data)).detail; } catch (_) { /* risposta non JSON */ }
      setErr(msgErrore(detail ? { response: { data: { detail } } } : e, 'Impossibile creare il file Excel'));
    } finally {
      setScarico(false);
    }
  };

  const ricarica = useCallback(() => setVersione(v => v + 1), []);
  const periodo = useMemo(() => ({ dal: f.dal, al: f.al }), [f.dal, f.al]);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const t = dati?.totale;

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1A1A2E', margin: 0 }}>Campagne</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {haPermesso('campagne.esporta_lead') && (
            <EsportaLead dal={f.dal} al={f.al} attribuzioneDal={filtri?.attribuzione_dal} />)}
          <button style={pulsante(false, scarico || !valido)} disabled={scarico || !valido} onClick={scaricaExcel}
            title="Per canale, per tipo di ricorso e segnalazioni, con i filtri attuali">
            <Download size={14} /> Excel</button>
          <button style={pulsante(false, carico || !valido)} disabled={carico || !valido} onClick={ricarica}>
            <RefreshCw size={14} className={carico ? 'spin' : ''} /> Aggiorna</button>
        </div>
      </div>

      <Sezione titolo="Filtri" colore={COLORI.filtri}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
          <label style={{ fontSize: 11, color: '#718096' }}>DAL<br />
            <input type="date" className="input-filtro" value={f.dal} onChange={e => set('dal', e.target.value)} /></label>
          <label style={{ fontSize: 11, color: '#718096' }}>AL<br />
            <input type="date" className="input-filtro" value={f.al} onChange={e => set('al', e.target.value)} /></label>
          {scorciatoie().map(s => (
            <button key={s.etichetta} style={pulsante(f.dal === s.dal && f.al === s.al)}
              onClick={() => setF(x => ({ ...x, dal: s.dal, al: s.al }))}>{s.etichetta}</button>
          ))}
          <label style={{ fontSize: 11, color: '#718096' }}>TIPO DI RICORSO<br />
            <select className="input-filtro" value={f.tipo} onChange={e => set('tipo', e.target.value)}>
              <option value="">Tutti</option>
              {(filtri?.tipi || []).map(x => <option key={x.id} value={x.id}>{x.nome}</option>)}
            </select></label>
          <label style={{ fontSize: 11, color: '#718096' }}>CANALE<br />
            <select className="input-filtro" value={f.canale} onChange={e => set('canale', e.target.value)}>
              <option value="">Tutti</option>
              {(filtri?.canali || []).map(c => <option key={c.chiave} value={c.chiave}>{c.etichetta}</option>)}
            </select></label>
        </div>
        {!valido && <div style={errore}>Periodo non valido: la data iniziale deve precedere quella finale.</div>}
      </Sezione>

      {err && <div style={errore}>{err}</div>}

      {dati && (
        <>
          {dati.segnalazioni.length > 0 && (
            <Sezione titolo={`Segnalazioni (${dati.segnalazioni.length})`} colore={COLORI.segnalazioni}>
              <div style={{ background: '#FFFBF0', border: '1px solid #F6E05E', borderRadius: 8, padding: '10px 14px' }}>
                {dati.segnalazioni.map((s, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#744210', display: 'flex', gap: 6, padding: '3px 0' }}>
                    <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} /> <span>{s.testo}</span>
                  </div>
                ))}
              </div>
            </Sezione>
          )}

          <Sezione titolo={`Sintesi dal ${fmtData(dati.periodo.dal)} al ${fmtData(dati.periodo.al)}`} colore={COLORI.sintesi}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              {[
                ['Spesa totale', fmtEur(t.spesa)],
                ['Persone uniche', fmtN(t.lead), `${fmtN(t.lead_invii).toString()} invii`],
                ['Adesioni', fmtN(t.adesioni ?? null)],
                ['Costo per adesione', <Rapporto r={t} campo="costo_per_adesione" fmt={fmtEur} />],
                ['Lead → adesioni', <Rapporto r={t} campo="lead_adesioni" fmt={fmtPerc} />],
              ].map(([titolo, valore, sub]) => (
                <div key={titolo} style={{ ...card, borderTop: `3px solid ${COLORI.sintesi}` }}>
                  <div style={{ fontSize: 11, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{titolo}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1A1A2E', marginTop: 4 }}>{valore}</div>
                  {sub && <div style={{ fontSize: 12, color: '#a0aec0' }}>{sub}</div>}
                </div>
              ))}
            </div>
          </Sezione>

          <Sezione titolo="Per canale" colore={COLORI.canali}><TabellaCanali dati={dati} /></Sezione>
          <TabellaTipi dati={dati} />
        </>
      )}

      {valido && <CostiManuali filtri={filtri} periodo={periodo} puoGestire={puoGestire} onCambio={ricarica} />}
      {puoGestire && filtri && <CampagneAdmin filtri={filtri} periodo={periodo} onCambio={ricarica} />}
    </div>
  );
}
